// The CALENDAR axis (design brief §5.2): a month grid — weeks as rows, seven day cells each — with the
// filtered slice's chips landing on their `dateField` day. The grid opens on the board's as-of month
// (never a fabricated now — the today button omits itself honestly when asOfDate is null). More than
// three chips on a day collapses to an overflow control ('+N more') that reveals the rest in place; the
// null-dateField items live in the shared UndatedTray below. Every chip is a button firing onOpenCard.
// Dates come from view-dates (pure, no `new Date()`); this file only arranges what those helpers return.

import { useMemo, useState } from 'react';
import type { WorkBoardProjection, WorkItemRow } from '../../projections.ts';
import {
  anchorMonth,
  monthGrid,
  parseIsoDay,
  partitionByDateField,
  stepMonth,
  type CalendarCell,
  type MonthAnchor,
} from '../view-dates.ts';
import { UndatedTray, ViewChip } from './UndatedTray.tsx';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;
const MAX_CHIPS = 3;

/** The chips a given ISO day owns, from the dated slice grouped by dateField. */
function chipsByDay(items: readonly WorkItemRow[]): Map<string, WorkItemRow[]> {
  const byDay = new Map<string, WorkItemRow[]>();
  for (const item of items) {
    if (item.dateField === null) continue;
    const bucket = byDay.get(item.dateField) ?? [];
    bucket.push(item);
    byDay.set(item.dateField, bucket);
  }
  return byDay;
}

function monthLabel(anchor: MonthAnchor): string {
  return `${MONTHS[anchor.month - 1]} ${String(anchor.year)}`;
}

function DayCell({
  cell,
  chips,
  onOpenCard,
}: {
  readonly cell: CalendarCell;
  readonly chips: readonly WorkItemRow[];
  readonly onOpenCard: (itemId: string) => void;
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  if (cell.iso === null) {
    return <div aria-hidden="true" className="min-h-20 rounded-sm bg-transparent" />;
  }
  const shown = expanded ? chips : chips.slice(0, MAX_CHIPS);
  const overflow = chips.length - shown.length;
  return (
    <div
      role="group"
      aria-label={`${cell.iso} — ${String(chips.length)} on this day`}
      data-day={cell.iso}
      className="min-h-20 rounded-sm border border-border bg-surface p-1.5"
    >
      <div className="mb-1 ritona-numeric text-xs text-text-subtle">{cell.day}</div>
      <div className="flex flex-col gap-1">
        {shown.map((item) => (
          <ViewChip key={item.id} item={item} onOpen={onOpenCard} />
        ))}
        {overflow > 0 ? (
          <button
            type="button"
            data-day-overflow={overflow}
            aria-expanded={false}
            onClick={() => setExpanded(true)}
            className="rounded-sm px-1 py-0.5 text-left text-xs font-medium text-text-muted hover:text-text"
          >
            +{overflow} more
          </button>
        ) : null}
      </div>
    </div>
  );
}

function MonthControls({
  anchor,
  asOfDate,
  onStep,
  onToday,
}: {
  readonly anchor: MonthAnchor;
  readonly asOfDate: string | null;
  readonly onStep: (delta: 1 | -1) => void;
  readonly onToday: () => void;
}): React.JSX.Element {
  return (
    <div className="mb-3 flex items-center gap-2">
      <button
        type="button"
        aria-label="Previous month"
        onClick={() => onStep(-1)}
        className="rounded-sm border border-border bg-surface px-2 py-1 text-sm text-text-muted hover:text-text"
      >
        ‹
      </button>
      <span
        data-cal-month={`${anchor.year}-${String(anchor.month).padStart(2, '0')}`}
        className="min-w-40 text-sm font-medium text-text"
      >
        {monthLabel(anchor)}
      </span>
      <button
        type="button"
        aria-label="Next month"
        onClick={() => onStep(1)}
        className="rounded-sm border border-border bg-surface px-2 py-1 text-sm text-text-muted hover:text-text"
      >
        ›
      </button>
      {asOfDate !== null ? (
        <button
          type="button"
          onClick={onToday}
          className="rounded-sm border border-border bg-surface px-3 py-1 text-sm font-medium text-text-muted hover:text-text"
        >
          Today
        </button>
      ) : null}
    </div>
  );
}

/** The seven-column month grid: the weekday header row, then a {@link DayCell} per calendar cell. */
function MonthGrid({
  anchor,
  byDay,
  onOpenCard,
}: {
  readonly anchor: MonthAnchor;
  readonly byDay: Map<string, WorkItemRow[]>;
  readonly onOpenCard: (itemId: string) => void;
}): React.JSX.Element {
  return (
    <div className="grid grid-cols-7 gap-1">
      {WEEKDAYS.map((label) => (
        <div
          key={label}
          className="ritona-numeric px-1 pb-1 text-xs uppercase tracking-eyebrow text-text-subtle"
        >
          {label}
        </div>
      ))}
      {monthGrid(anchor).flatMap((week, wi) =>
        week.map((cell, ci) => (
          <DayCell
            key={cell.iso ?? `blank-${String(wi)}-${String(ci)}`}
            cell={cell}
            chips={byDay.get(cell.iso ?? '') ?? []}
            onOpenCard={onOpenCard}
          />
        )),
      )}
    </div>
  );
}

export interface CalendarViewProps {
  readonly board: WorkBoardProjection;
  /** The filtered slice — the same rows every axis renders. */
  readonly items: readonly WorkItemRow[];
  readonly onOpenCard: (itemId: string) => void;
}

/** The month-grid axis: controls, weekday header, the weeks, then the undated tray. */
export function CalendarView({ board, items, onOpenCard }: CalendarViewProps): React.JSX.Element {
  const { dated, undated } = useMemo(() => partitionByDateField(items), [items]);
  const opening = useMemo(
    () =>
      anchorMonth(
        board.asOfDate,
        dated.map((item) => item.dateField),
      ),
    [board.asOfDate, dated],
  );
  const [anchor, setAnchor] = useState<MonthAnchor | null>(opening);
  const current = anchor ?? opening;
  const byDay = useMemo(() => chipsByDay(dated), [dated]);
  const asOfParts = parseIsoDay(board.asOfDate);

  const onToday = (): void => {
    if (asOfParts !== null) setAnchor({ year: asOfParts.year, month: asOfParts.month });
  };

  return (
    <section aria-label="Calendar" data-axis-view="calendar">
      {current !== null ? (
        <>
          <MonthControls
            anchor={current}
            asOfDate={board.asOfDate}
            onStep={(delta) => setAnchor(stepMonth(current, delta))}
            onToday={onToday}
          />
          <MonthGrid anchor={current} byDay={byDay} onOpenCard={onOpenCard} />
        </>
      ) : null}
      <UndatedTray items={undated} onOpenCard={onOpenCard} />
    </section>
  );
}
